import { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { StyledButton } from "@components";
import { AppDispatch, checkPayment } from "@redux";
import { breakpoint } from "@utilities";
import { useCheckout } from "../CheckoutContext";

interface IProps {
    btnTitle?: string;
    onBtnClick?: () => void;
    page?: string;
    paymentOption?: string;
    disableNext?: boolean;
    isSubmitting?: boolean;
    totalPrice?: number;
}

export const CheckoutItemsActions = memo((props: IProps) => {
    const { btnTitle, isSubmitting, onBtnClick, disableNext } = props;
    const [showHelp, setShowHelp] = useState(false);

    const {
        activeDiscount,
        orderDiscount,
        isOrderDiscountLoading,
        discountedPrice,
        originalPrice,
    } = useCheckout();
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        dispatch(checkPayment());
    }, []);

    return (
        <CheckoutActionsStyled>
            {showHelp && (
                <div className="help-popup-overlay" onClick={() => setShowHelp(false)}>
                    <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
                        <p>
                            The final discount shown is based on the items in your cart that are
                            eligible for the offer. Some products may not qualify, while others
                            might have reduced discounts due to operator limitations. That’s why the
                            final discount may be lower than expected — it’s automatically adjusted
                            based on eligible items only.
                        </p>
                        <StyledButton
                            $borderRadius="4px"
                            color={["#436CB2 0%", "#28B7C3 100%"]}
                            onClick={() => setShowHelp(false)}
                        >
                            Got it
                        </StyledButton>
                    </div>
                </div>
            )}
            <div className="wrapper">
                <PriceBlockStyled>
                    <div className="price-label-wrapper">
                        <span className="price-label">Price</span>
                        {(activeDiscount ?? 0) > 0 && (
                            <div className="price-discount-badge-wrapper">
                                {isOrderDiscountLoading ? (
                                    <div className="loading-spinner"></div>
                                ) : (
                                    (orderDiscount ?? 0) > 0 && (
                                        <>
                                            <span className="price-discount-badge">
                                                {orderDiscount}% off
                                            </span>
                                            <QuestionMarkHelper>
                                                <img
                                                    onClick={() => setShowHelp(true)}
                                                    src="/images/HelpIcon.svg"
                                                    alt="info"
                                                />
                                            </QuestionMarkHelper>
                                        </>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                    {!isOrderDiscountLoading && (
                        <div className="price-value-wrapper">
                            <span className="price-discounted-value">
                                ${(discountedPrice ?? originalPrice).toFixed(2)}
                            </span>
                            {/* TODO: show original price if discounted price is available and less than original price */}
                            {!!discountedPrice && originalPrice > discountedPrice && (
                                <span className="price-original-value">
                                    ${originalPrice.toFixed(2)}
                                </span>
                            )}
                        </div>
                    )}
                </PriceBlockStyled>
                <CheckoutButtonStyled
                    $borderRadius="4px"
                    color={["#436CB2 0%", "#28B7C3 100%"]}
                    onClick={onBtnClick}
                    disabled={disableNext || isSubmitting || isOrderDiscountLoading}
                >
                    {btnTitle}
                </CheckoutButtonStyled>
            </div>
        </CheckoutActionsStyled>
    );
});

const CheckoutActionsStyled = styled.div`
    background-color: #fff;
    border-radius: 8px;

    ${breakpoint.breakIpad`
        width: 270px;
    `}

    .wrapper {
        display: flex;
        flex-direction: column;
        padding: 12px 16px;
        gap: 10px;

        ${breakpoint.breakMobile`
            flex-direction : row;
        `}
    }

    .totalPrice {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        ${breakpoint.breakMobile`
            width: 200px;
            flex-direction : column;
            align-items: start;
        `}
        .text {
            font-size: 14px;
            color: #6e7375;
            font-weight: 500;
        }

        .value {
            font-size: 24px;
            font-weight: 500;
            font-family: Plus Jakarta Sans;
        }
    }
    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
    }

    .help-popup-content {
        background: white;
        padding: 2rem;
        border-radius: 8px;
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;   
        `}
        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }
        h3 {
            margin-bottom: 1rem;
        }

        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }
`;

const CheckoutButtonStyled = styled(StyledButton)`
    margin-top: 0px;
    height: 54px;
`;

const PriceBlockStyled = styled.div`
    font-family: Poppins;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;

    .price-label-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-label {
            font-size: 0.875rem;
            color: var(--fg-muted-color);
            font-weight: 300;
        }

        .price-discount-badge-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;

            .price-discount-badge {
                font-size: 0.75rem;
                padding: 3px 8px;
                background-color: var(--primary-color);
                color: white;
                border-radius: 16px;
            }

            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid var(--primary-color);
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }

    .price-value-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-discounted-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--primary-color);
        }

        .price-original-value {
            font-size: 1rem;
            font-weight: 400;
            color: var(--fg-subtle-color);
            text-decoration: line-through;
        }
    }
`;
const QuestionMarkHelper = styled.div`
    cursor: pointer;
`;
